import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ClientPayment,
  CreateClientPaymentInput,
  UpdateClientPaymentInput,
  EventClientLedger,
  ClientReceivablesReport,
} from '@peles/shared';
import * as api from '../api/client-payments';

const ledgerQueryKey = (eventId: string) => ['clientPayments', 'ledger', eventId];
const eventListQueryKey = (eventId: string) => ['clientPayments', 'list', eventId];

function invalidateEventLedger(queryClient: ReturnType<typeof useQueryClient>, eventId?: string) {
  if (eventId) {
    queryClient.invalidateQueries({ queryKey: ledgerQueryKey(eventId) });
    queryClient.invalidateQueries({ queryKey: eventListQueryKey(eventId) });
  }
  queryClient.invalidateQueries({ queryKey: ['clientPayments'] });
  queryClient.invalidateQueries({ queryKey: ['clientReceivables'] });
  queryClient.invalidateQueries({ queryKey: ['expenses'] });
}

export function useClientPaymentsByEvent(eventId: string | undefined) {
  return useQuery<ClientPayment[]>({
    queryKey: eventListQueryKey(eventId ?? ''),
    queryFn: () => api.listClientPaymentsByEvent(eventId!),
    enabled: !!eventId,
  });
}

export function useEventClientLedger(eventId: string | undefined) {
  return useQuery<EventClientLedger>({
    queryKey: ledgerQueryKey(eventId ?? ''),
    queryFn: () => api.getEventClientLedger(eventId!),
    enabled: !!eventId,
  });
}

export function useClientReceivables() {
  return useQuery<ClientReceivablesReport>({
    queryKey: ['clientReceivables'],
    queryFn: () => api.getClientReceivables(),
  });
}

export function useCreateClientPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClientPaymentInput) => api.createClientPayment(input),
    onSuccess: (payment) => invalidateEventLedger(queryClient, payment.eventId),
  });
}

export function useUpdateClientPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateClientPaymentInput }) =>
      api.updateClientPayment(id, updates),
    onSuccess: (payment) => invalidateEventLedger(queryClient, payment.eventId),
  });
}

export function useDeleteClientPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; eventId: string }) => api.deleteClientPayment(id),
    onSuccess: (_data, variables) => invalidateEventLedger(queryClient, variables.eventId),
  });
}
